/**
 * Datas de calendário como texto ISO (AAAA-MM-DD), sem fuso: evita o erro clássico do dia anterior ao converter UTC.
 * "Hoje" é sempre o dia em Brasília (America/Sao_Paulo), onde a equipe trabalha.
 */

export type IsoDate = string;

const DAY = 86_400_000;
const toUtc = (d: IsoDate) => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
const fromUtc = (ms: number): IsoDate => new Date(ms).toISOString().slice(0, 10);

export const isIsoDate = (s: unknown): s is IsoDate => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(toUtc(s));

export function todayBR(now: Date = new Date()): IsoDate {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export const addDays = (d: IsoDate, n: number): IsoDate => fromUtc(toUtc(d) + n * DAY);
/** Dias corridos de `a` até `b` (b − a). */
export const diffDays = (a: IsoDate, b: IsoDate): number => Math.round((toUtc(b) - toUtc(a)) / DAY);
export const weekday = (d: IsoDate): number => new Date(toUtc(d)).getUTCDay(); // 0 = domingo
export const isWeekend = (d: IsoDate) => { const w = weekday(d); return w === 0 || w === 6; };

/** Segunda-feira da semana de `d` (o apontamento de horas é por semana, de segunda a domingo). */
export const weekStart = (d: IsoDate): IsoDate => addDays(d, -((weekday(d) + 6) % 7));
export const weekDays = (monday: IsoDate): IsoDate[] => Array.from({ length: 7 }, (_, i) => addDays(monday, i));

/** Dias úteis entre `start` e `end`, inclusive, sem fins de semana e feriados. */
export function businessDays(start: IsoDate, end: IsoDate, holidays: ReadonlySet<IsoDate> = new Set()): number {
  if (end < start) return 0;
  let n = 0;
  for (let d = start; d <= end; d = addDays(d, 1)) if (!isWeekend(d) && !holidays.has(d)) n++;
  return n;
}

export const minDate = (...ds: (IsoDate | null | undefined)[]): IsoDate | null => ds.filter((d): d is IsoDate => !!d).sort()[0] ?? null;
export const maxDate = (...ds: (IsoDate | null | undefined)[]): IsoDate | null => ds.filter((d): d is IsoDate => !!d).sort().at(-1) ?? null;

/** dd/mm/aaaa para a tela. */
export const fmtDateBR = (d: IsoDate | null | undefined): string => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');

/**
 * Meses e dias de calendário entre duas datas: soma meses à data inicial (no último dia do mês quando ele não existe,
 * 31/01 + 1 mês = 28/02) e conta os dias que sobram. De 12/08 a 15/10 = 2 meses e 3 dias.
 */
export function elapsed(from: IsoDate, to: IsoDate): { months: number; days: number } {
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  const end = Date.UTC(ty, tm - 1, td);
  if (end <= Date.UTC(fy, fm - 1, fd)) return { months: 0, days: 0 };
  const anchor = (m: number) => {
    const last = new Date(Date.UTC(fy, fm - 1 + m + 1, 0)).getUTCDate();
    return Date.UTC(fy, fm - 1 + m, Math.min(fd, last));
  };
  let months = (ty - fy) * 12 + (tm - fm);
  while (months > 0 && anchor(months) > end) months--;
  return { months, days: Math.round((end - anchor(months)) / 86_400_000) };
}

/** "2 meses e 3 dias", "1 mês", "15 dias", "menos de 1 dia". */
export function fmtElapsed(from: IsoDate, to: IsoDate): string {
  const { months, days } = elapsed(from, to);
  const m = months ? `${months} ${months === 1 ? 'mês' : 'meses'}` : '';
  const d = days ? `${days} ${days === 1 ? 'dia' : 'dias'}` : '';
  return m && d ? `${m} e ${d}` : m || d || 'menos de 1 dia';
}

/** Formato de data e de hora da plataforma (Administração › Geral). */
export type DateOrder = 'dmy' | 'mdy';
export type TimeFormat = '24h' | '12h';
export const DATE_ORDER_LABEL: Record<DateOrder, string> = { dmy: 'dd/mm/aaaa', mdy: 'mm/dd/aaaa' };
export const TIME_FORMAT_LABEL: Record<TimeFormat, string> = { '24h': '24 horas (14:30)', '12h': '12 horas (2:30 PM)' };

/** AAAA-MM-DD → dd/mm/aaaa ou mm/dd/aaaa. */
export function formatIsoDate(d: IsoDate | null | undefined, order: DateOrder = 'dmy'): string {
  if (!d) return '—';
  const [y, m, day] = [d.slice(0, 4), d.slice(5, 7), d.slice(8, 10)];
  return order === 'mdy' ? `${m}/${day}/${y}` : `${day}/${m}/${y}`;
}

/** "15/10/2026" (ou "10/15/2026") → "2026-10-15"; data que não existe ou incompleta → null. Aceita ano com 2 dígitos (26 → 2026). */
export function parseDateText(text: string, order: DateOrder = 'dmy'): IsoDate | null {
  const m = /^\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})\s*$/.exec(text);
  if (!m) return null;
  const [a, b] = [Number(m[1]), Number(m[2])];
  const [day, month] = order === 'mdy' ? [b, a] : [a, b];
  const year = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) return null;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` as IsoDate;
}

/** "14:30" → "2:30 PM" no formato de 12 horas. */
export function formatClock(hhmm: string, fmt: TimeFormat = '24h'): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m || fmt === '24h') return hhmm.slice(0, 5);
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}
